import React from 'react';
import { useAppContext } from '../../hooks/useAppContext';

const labelByMode = {
  light: 'Light',
  dark: 'Dark',
  system: 'System',
} as const;

const iconByMode = {
  light: (
    <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4" stroke="currentColor" strokeWidth="1.8">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2.5M12 19.5V22M4.93 4.93l1.77 1.77M17.3 17.3l1.77 1.77M2 12h2.5M19.5 12H22M4.93 19.07l1.77-1.77M17.3 6.7l1.77-1.77" strokeLinecap="round" />
    </svg>
  ),
  dark: (
    <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4" stroke="currentColor" strokeWidth="1.8">
      <path d="M21 12.8A9 9 0 1111.2 3 7 7 0 0021 12.8z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  system: (
    <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4" stroke="currentColor" strokeWidth="1.8">
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M8 20h8M12 16v4" strokeLinecap="round" />
    </svg>
  ),
} as const;

export const ThemeToggle: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  const { themeMode, cycleThemeMode } = useAppContext();
  const knobPositionClass =
    themeMode === 'light'
      ? 'translate-x-0'
      : themeMode === 'dark'
        ? 'translate-x-[1.55rem]'
        : 'translate-x-[3.1rem]';

  return (
    <button
      type="button"
      onClick={cycleThemeMode}
      className={`inline-flex items-center gap-3 rounded-full border border-gray-200 bg-white/95 px-2 py-2 text-sm font-bold text-gray-700 shadow-sm transition hover:border-indigo-300 hover:text-indigo-700 dark:border-slate-700 dark:bg-slate-900/90 dark:text-slate-100 dark:hover:border-sky-500 dark:hover:text-sky-300 ${
        compact ? 'gap-2 px-2 py-1.5 text-xs' : ''
      }`}
      aria-label={`Switch theme mode. Current mode: ${labelByMode[themeMode]}`}
      title={`Theme: ${labelByMode[themeMode]}`}
    >
      <span
        className={`relative flex shrink-0 items-center rounded-full border border-gray-200 bg-gray-100/90 p-0.5 dark:border-slate-700 dark:bg-slate-800/90 ${
          compact ? 'h-7 w-[4.35rem]' : 'h-8 w-[5.1rem]'
        }`}
        aria-hidden="true"
      >
        <span
          className={`absolute left-0.5 top-0.5 flex items-center justify-center rounded-full bg-white text-gray-700 shadow-sm transition-transform duration-300 dark:bg-slate-950 dark:text-slate-100 ${knobPositionClass} ${
            compact ? 'h-5.5 w-5.5' : 'h-6 w-6'
          }`}
        >
          {iconByMode[themeMode]}
        </span>
        <span className="flex w-full items-center justify-between px-1.5 text-[9px] font-black uppercase tracking-[0.18em] text-gray-400 dark:text-slate-500">
          <span>L</span>
          <span>D</span>
          <span>S</span>
        </span>
      </span>
      <span className={compact ? 'hidden sm:inline' : ''}>{labelByMode[themeMode]}</span>
    </button>
  );
};

export default ThemeToggle;
